import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import api, { setAccessToken, setRefreshToken, setTenantSubdomain, clearAuthSession } from "../services/api";

type Role = "super_admin" | "admin" | "manager" | "staff" | "cashier" | null;

export interface UserProfile {
  id: string;
  uid?: string;
  email: string;
  role: Role;
  displayName?: string;
  photoURL?: string | null;
  tenantId?: string;
  subdomain?: string;
  branchId?: string | null;
}

interface AuthContextValue {
  user: UserProfile | null;
  role: Role;
  idToken: string | null;
  subdomain: string | null;
  tenantId: string | null;
  branchId: string | null;
  userProfile: UserProfile | null;
  isLoading: boolean;
  login: (email: string, password?: string, subdomain?: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const STORAGE_KEYS = {
  ACCESS_TOKEN: "accessToken",
  REFRESH_TOKEN: "refreshToken",
  USER_PROFILE: "userProfile",
  SUBDOMAIN: "tenantSubdomain",
  TENANT_ID: "tenantId",
  ROLE: "userRole",
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.USER_PROFILE);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const [role, setRole] = useState<Role>(() => {
    return (localStorage.getItem(STORAGE_KEYS.ROLE) as Role) || userProfile?.role || null;
  });

  const [idToken, setIdToken] = useState<string | null>(() => {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  });

  const [subdomain, setSubdomain] = useState<string | null>(() => {
    return localStorage.getItem(STORAGE_KEYS.SUBDOMAIN);
  });

  const [tenantId, setTenantId] = useState<string | null>(() => {
    return localStorage.getItem(STORAGE_KEYS.TENANT_ID);
  });

  const [branchId, setBranchId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Synchronize state on initial load
  useEffect(() => {
    const initializeAuth = async () => {
      const storedAccessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      const storedRefreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

      if (storedAccessToken) {
        setAccessToken(storedAccessToken);
        setIdToken(storedAccessToken);
      }

      if (storedRefreshToken) {
        setRefreshToken(storedRefreshToken);
      }

      const activeSubdomain = localStorage.getItem(STORAGE_KEYS.SUBDOMAIN);
      if (activeSubdomain) {
        setTenantSubdomain(activeSubdomain);
        setSubdomain(activeSubdomain);
      }

      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = useCallback(async (email: string, password?: string, inputSubdomain?: string) => {
    const payload: Record<string, any> = { email: email.trim() };
    if (password) payload.password = password;
    if (inputSubdomain) payload.subdomain = inputSubdomain;

    const activeSub = inputSubdomain || localStorage.getItem(STORAGE_KEYS.SUBDOMAIN);
    if (activeSub) {
      setTenantSubdomain(activeSub);
    }

    const res = await api.post<{
      user: any;
      role: Role;
      accessToken: string;
      refreshToken: string;
      subdomain?: string;
      tenantId?: string;
      branchId?: string | null;
    }>("/api/auth/session", payload);

    const sessionData = res.data;

    const newAccessToken = sessionData.accessToken;
    const newRefreshToken = sessionData.refreshToken;
    const activeRole = sessionData.role || sessionData.user?.role;
    const resolvedSubdomain = sessionData.subdomain || sessionData.user?.subdomain || inputSubdomain || null;
    const resolvedTenantId = sessionData.tenantId || sessionData.user?.tenantId || null;
    const resolvedBranchId = sessionData.branchId || sessionData.user?.branchId || null;

    setAccessToken(newAccessToken);
    setRefreshToken(newRefreshToken);
    if (resolvedSubdomain) setTenantSubdomain(resolvedSubdomain);

    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, newAccessToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, newRefreshToken);
    if (activeRole) localStorage.setItem(STORAGE_KEYS.ROLE, activeRole);
    if (activeRole === "super_admin") {
      localStorage.setItem("platformToken", newAccessToken);
    }
    if (resolvedSubdomain) localStorage.setItem(STORAGE_KEYS.SUBDOMAIN, resolvedSubdomain);
    if (resolvedTenantId) localStorage.setItem(STORAGE_KEYS.TENANT_ID, resolvedTenantId);

    const userId = sessionData.user?.id || sessionData.user?.uid || "user-id";
    const profile: UserProfile = {
      id: userId,
      uid: userId,
      email: sessionData.user?.email || email,
      displayName: sessionData.user?.displayName || sessionData.user?.email || email,
      role: activeRole,
      tenantId: resolvedTenantId || undefined,
      subdomain: resolvedSubdomain || undefined,
      branchId: resolvedBranchId,
    };

    localStorage.setItem(STORAGE_KEYS.USER_PROFILE, JSON.stringify(profile));

    setIdToken(newAccessToken);
    setRole(activeRole);
    setSubdomain(resolvedSubdomain);
    setTenantId(resolvedTenantId);
    setBranchId(resolvedBranchId);
    setUserProfile(profile);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/api/auth/logout");
    } catch (err) {
      console.warn("Backend logout request failed:", err);
    } finally {
      clearAuthSession();

      setIdToken(null);
      setRole(null);
      setSubdomain(null);
      setTenantId(null);
      setBranchId(null);
      setUserProfile(null);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user: userProfile,
        role,
        idToken,
        subdomain,
        tenantId,
        branchId,
        userProfile,
        isLoading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
